import { supabase } from '@/integrations/supabase/client';
import type { AppDatabase } from '@/integrations/supabase/app-types';
import {
  FormAssemblyForm,
  FormDetail,
  FormDetailData,
  FormBackup,
  FormSummary,
  ConnectorDefinition,
  FormField,
  FormNotification,
  FormSettings,
} from '@/types/formassembly';
import { logger } from '@/lib/logger';
import { createServiceError } from '@/lib/apiErrors';

// Define a type for common error payloads from Supabase functions or upstream APIs
interface ErrorPayload {
  error?: string;
  message?: string;
  code?: string;
  [key: string]: unknown; // Use unknown for better type safety than any
}

// Check if user has FA credentials by querying the new table
export const checkFormAssemblyConnection = async (): Promise<boolean | null> => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
    return false;
  }
  const { data, error } = await supabase
    .from('user_formassembly_credentials')
    .select('id')
    .eq('user_id', user.id)
    .maybeSingle();

  if (error) {
    logger.error("Error checking FormAssembly connection status:", error);
    return null;
  }
  return !!data;
};

export const fetchFormAssemblyForms = async (): Promise<FormAssemblyForm[]> => {
  logger.debug("Attempting to invoke formassembly-proxy to fetch forms...");
  const { data, error } = await supabase.functions.invoke('formassembly-proxy');

  if (error) {
    logger.error("Error invoking formassembly-proxy function:", error);
    let responseData: ErrorPayload | null = null;
    try {
      const jsonFunc = (error as any)?.context?.json;
      if (typeof jsonFunc === 'function') {
        responseData = await jsonFunc();
      }
    } catch (e) {
      logger.error('[FORMS FETCH] Failed to parse error response:', e);
    }

    if (error.message?.includes("NOT_CONNECTED") || error.message?.includes("REAUTH_REQUIRED") || responseData?.code === "REAUTH_REQUIRED" || data?.code === "REAUTH_REQUIRED") {
        throw new Error("REAUTH_REQUIRED");
    }
    const errorMessage = responseData?.error || data?.error || error.message || "Failed to fetch FormAssembly forms";
    throw createServiceError(errorMessage, error);
  }

  if (data && data.forms) {
    logger.debug("Successfully invoked formassembly-proxy, forms received:", data.forms.length);
    return data.forms as FormAssemblyForm[];
  }
  
  logger.warn("formassembly-proxy response did not contain a 'forms' array or data was null:", data);
  if (data && data.error) {
      if (data.code === "REAUTH_REQUIRED") throw new Error("REAUTH_REQUIRED");
      throw new Error(data.error);
  }
  return [];
};

export const fetchFormAssemblyFormDetails = async (formId: number): Promise<FormDetailData> => {
  logger.debug(`Attempting to invoke formassembly-form-view-proxy for formId: ${formId}`);
  const { data, error } = await supabase.functions.invoke(`formassembly-form-view-proxy?formId=${formId}`, {
    method: 'GET',
  });

  if (error) {
    logger.error(`Error invoking formassembly-form-view-proxy for formId ${formId}:`, error);
    let responseData: ErrorPayload | null = null;
    try {
      const jsonFunc = (error as any)?.context?.json;
      if (typeof jsonFunc === 'function') {
        responseData = await jsonFunc();
      }
    } catch (e) {
      logger.error('[FORM VIEW] Failed to parse error response:', e);
    }

    if (error.message?.includes("NOT_CONNECTED") || error.message?.includes("REAUTH_REQUIRED") || responseData?.code === "REAUTH_REQUIRED" || (data as ErrorPayload)?.code === "REAUTH_REQUIRED") {
        throw new Error("REAUTH_REQUIRED");
    }
    const errorMessage = responseData?.error || (data as ErrorPayload)?.error || error.message || `Failed to fetch form details for form ${formId}`;
    throw createServiceError(errorMessage, error);
  }

  // Refined runtime checks for the structure of 'data'
  if (
    !data || typeof data !== 'object' || data === null ||
    !('formDataJson' in data) || typeof (data as Record<string, unknown>).formDataJson !== 'object' || (data as Record<string, unknown>).formDataJson === null ||
    !('form' in (data as { formDataJson: Record<string, unknown> }).formDataJson) || // Check if 'form' property exists
    !('formDataXml' in data) || typeof (data as Record<string, unknown>).formDataXml !== 'string'
  ) {
    logger.error('Invalid data structure received from formassembly-form-view-proxy. Data:', data);
    const responseData = data as ErrorPayload | null;
    if (responseData && responseData.error) {
        if (responseData.code === "REAUTH_REQUIRED") throw new Error("REAUTH_REQUIRED");
        throw new Error(`Failed to fetch form details: ${responseData.error}`);
    }
    throw new Error('Failed to fetch form details: Invalid or incomplete data structure received from server.');
  }
  
  // Type assertion to the expected structure for easier access, aligning with FormDetailData types
  const structuredData = data as {
    formDataJson: {
      form: FormDetail;
      fields: FormField[];
      notifications: FormNotification[];
      processing: unknown;
      [key: string]: unknown;
    };
    formDataXml: string;
    connectorDefinitions?: ConnectorDefinition[]; // Correctly typed for FormDetailData.connectors
    [key: string]: unknown;
  };

  logger.debug(`Successfully invoked formassembly-form-view-proxy for formId ${formId}, details received. Transforming data...`);

  const transformedData: FormDetailData = {
    form: structuredData.formDataJson.form,
    fields: structuredData.formDataJson.fields || [],
    connectors: structuredData.connectorDefinitions || [],
    themes: [], // No longer fetched by view-proxy; use backup_themes for historical data
    notifications: structuredData.formDataJson.notifications || [],
    processing: structuredData.formDataJson.processing || {},
    rawJson: structuredData.formDataJson, // rawJson in FormDetailData is 'any', so this is fine
    rawXml: structuredData.formDataXml,
  };

  return transformedData;
};

export const backupFormAssemblyForm = async (formId: number): Promise<{ success: boolean; backupId: string; message: string }> => {
  logger.debug(`Attempting to invoke formassembly-backup-form-proxy for formId: ${formId}`);
  const { data, error } = await supabase.functions.invoke('formassembly-backup-form-proxy', {
    method: 'POST',
    body: { formId: formId.toString() },
  });

  if (error) {
    logger.error(`Error invoking formassembly-backup-form-proxy for formId ${formId}:`, error);

    // Extract error details - error.context.json is a FUNCTION that needs to be called
    let responseData: ErrorPayload | null = null;
    try {
      const jsonFunc = (error as any)?.context?.json;
      if (typeof jsonFunc === 'function') {
        responseData = await jsonFunc();
      }
    } catch (e) {
      logger.error('[BACKUP] Failed to parse error response:', e);
    }

    const errorBody = responseData?.error || data?.error;

    // Check for REAUTH_REQUIRED
    if (error.message?.includes("NOT_CONNECTED") || error.message?.includes("REAUTH_REQUIRED") || responseData?.code === "REAUTH_REQUIRED" || data?.code === "REAUTH_REQUIRED") {
        throw new Error("REAUTH_REQUIRED");
    }

    // Build a helpful error message for users
    if (errorBody) {
      // We have a detailed error from the edge function - pass it through
      throw createServiceError(errorBody, error);
    } else {
      // Generic error - provide context
      throw createServiceError(`Unable to backup form ${formId}. The server returned an error (HTTP 500). Please try again or contact support if this persists.`, error);
    }
  }

  if (data && data.success) {
    logger.debug(`Successfully invoked formassembly-backup-form-proxy for formId ${formId}, backup ID: ${data.backupId}`);
    return data as { success: boolean; backupId: string; message: string };
  }

  const failureMessage = data?.error || data?.message || `Backup for form ${formId} failed or returned an unexpected response.`;
  throw new Error(failureMessage);
};

export const fetchFormBackups = async (formIdExternal: string): Promise<FormBackup[]> => {
  logger.debug(`Fetching backups for form_id_external: ${formIdExternal}`);
  const { data, error } = await supabase
    .from('form_backups')
    .select('*, backup_connectors(*), backup_themes(*)')
    .eq('form_id_external', formIdExternal)
    .order('created_at', { ascending: false });

  if (error) {
    logger.error('Error fetching form backups:', error);
    throw new Error(error.message);
  }
  logger.debug(`Fetched ${data?.length || 0} backups for form ${formIdExternal}`);
  return data || [];
};

export const fetchAllUserFormBackups = async (): Promise<FormBackup[]> => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
    logger.debug('fetchAllUserFormBackups: No user session, returning empty array.');
    return [];
  }

  logger.debug(`Fetching all backups for user_id: ${user.id}`);
  const { data, error } = await supabase
    .from('form_backups')
    .select('*, backup_connectors(*), backup_themes(*)')
    .eq('user_id', user.id)
    .eq('provider', 'formassembly')
    .order('created_at', { ascending: false });

  if (error) {
    logger.error('Error fetching all user form backups:', error);
    throw new Error(error.message);
  }
  logger.debug(`Fetched ${data?.length || 0} total backups for user ${user.id}`);
  return data || [];
};

export const fetchFormSummary = async (formIdExternal: string): Promise<FormSummary | null> => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
    logger.debug('fetchFormSummary: No user session, cannot fetch summary.');
    return null;
  }

  logger.debug(`Fetching stored summary for form_id_external: ${formIdExternal} by user ${user.id}`);
  const { data, error } = await supabase
    .from('form_summaries')
    .select('summary_text, last_generated_at')
    .eq('user_id', user.id)
    .eq('form_id_external', formIdExternal)
    .maybeSingle();

  if (error) {
    logger.error('Error fetching stored form summary:', error);
    // Don't throw, just return null if not found or error
    return null;
  }
  
  if (data) {
    logger.debug(`Fetched stored summary for form ${formIdExternal}`);
    return data as FormSummary;
  }
  return null;
};

export const summarizeFormWithClaude = async ({ formJson, formIdExternal }: { formJson: Record<string, unknown>; formIdExternal: string }): Promise<{ summary: string | null; rateLimitHit?: boolean }> => {
  if (import.meta.env.VITE_ENABLE_FORMASSEMBLY_AI_SUMMARIES !== 'true') {
    throw new Error('FormAssembly AI summaries are disabled.');
  }

  logger.debug(`Attempting to invoke summarize-form-claude function for formIdExternal: ${formIdExternal}...`);
  const { data, error } = await supabase.functions.invoke('summarize-form-claude', {
    method: 'POST',
    body: { formJson, formIdExternal },
  });

  if (error) {
    logger.error("Error invoking summarize-form-claude function:", error);
    // Check if the error object from the function has more details
    const errorResponse = error?.context?.json ? error.context.json : null;
    const errorMessage = errorResponse?.error || error.message || "Failed to generate form summary.";
    const rateLimitHit = errorResponse?.rateLimitHit || (error.context?.status === 429);

    if (errorMessage.includes("Function not found")) {
      throw new Error("The AI summary function is not available. It might still be deploying.");
    }
    const customError = createServiceError(errorMessage, error);
    if (rateLimitHit) {
        customError.rateLimitHit = true;
    }
    throw customError;
  }

  if (data && typeof data.summary === 'string') {
    logger.debug("Successfully invoked summarize-form-claude, summary received.");
    return { summary: data.summary, rateLimitHit: (data as { rateLimitHit?: boolean }).rateLimitHit };
  }
  if (data && (data as ErrorPayload).error) { 
    logger.warn("summarize-form-claude returned an error payload:", (data as ErrorPayload).error);
    const customError = new Error((data as ErrorPayload).error) as Error & { rateLimitHit?: boolean; status?: number };
    if ((data as {rateLimitHit?: boolean}).rateLimitHit) {
        customError.rateLimitHit = true;
    }
    customError.status = (data as {status?: number}).status || ((data as {rateLimitHit?: boolean}).rateLimitHit ? 429 : 500);
    throw customError;
  }

  const failureMessage = "Summary generation failed or returned an unexpected response.";
  throw new Error(failureMessage);
};

export const restoreFormFromBackup = async (backup: FormBackup, newFormName: string): Promise<FormAssemblyForm> => {
  logger.debug(`Attempting to restore form from backup ID: ${backup.id}`);
  
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) {
    throw new Error("User not authenticated. Cannot restore form.");
  }

  const formDetails = backup.form_data_json?.form as FormDetail | undefined;
  
  const connectorsToRestore = backup.backup_connectors?.map(c => ({
    name: c.name,
    connectorData: c.connector_definition_json,
  })) || [];

  const { data, error } = await supabase.functions.invoke('formassembly-restore-form-proxy', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${session.access_token}`,
    },
    body: { 
      formName: newFormName,
      xmlData: backup.form_data_xml,
      backupId: backup.id, // Add backup ID for UUID-based field transformation
      builder_version: formDetails?.builder_version,
      language: formDetails?.language,
      display_status: formDetails?.display_status,
      use_ssl: formDetails?.use_ssl,
      moderation_status: formDetails?.moderation_status,
      connectors: connectorsToRestore,
    },
  });

  if (error) {
    logger.error("Error invoking formassembly-restore-form-proxy:", error);
    const responseData = (error as { context?: { json?: ErrorPayload } })?.context?.json;
    if (responseData?.code === 'REAUTH_REQUIRED') {
      throw new Error("REAUTH_REQUIRED");
    }
    const errorMessage = responseData?.error || error.message || `Failed to restore form from backup ${backup.id}`;
    throw createServiceError(errorMessage, error);
  }
  
  const result = data as { restoredForm: { form_id: string; name: string } } & { success?: boolean; message?: string };

  if (result && result.success) {
    logger.debug(`Successfully invoked formassembly-restore-form-proxy. Message: ${result.message}`);
    // Convert form_id to id to match FormAssemblyForm interface
    return {
      id: parseInt(result.restoredForm.form_id, 10),
      name: result.restoredForm.name,
    } as FormAssemblyForm;
  }

  const failureMessage = (data as ErrorPayload)?.error || `Restore failed for backup ${backup.id} with an unexpected response.`;
  throw new Error(failureMessage);
};

/**
 * Fetch form settings (extended backup data not in FormAssembly API)
 */
export const fetchFormSettings = async (formIdExternal: string): Promise<FormSettings | null> => {
  const { data, error } = await supabase
    .from('form_settings')
    .select('*')
    .eq('form_id_external', formIdExternal)
    .maybeSingle();

  if (error) {
    logger.error('Error fetching form settings:', error);
    throw new Error(error.message);
  }

  return data as FormSettings | null;
};

/**
 * Save or update form settings (extended backup data not in FormAssembly API)
 */
export const saveFormSettings = async (
  formIdExternal: string,
  settings: Partial<Omit<FormSettings, 'id' | 'user_id' | 'form_id_external' | 'created_at' | 'updated_at'>>
): Promise<FormSettings> => {
  const { data: { user }, error: userError } = await supabase.auth.getUser();

  if (userError || !user) {
    throw new Error('User not authenticated');
  }

  // Check if settings already exist
  const { data: existing } = await supabase
    .from('form_settings')
    .select('id')
    .eq('form_id_external', formIdExternal)
    .eq('user_id', user.id)
    .maybeSingle();
  const existingSettings = existing as { id: string } | null;

  if (existingSettings) {
    // Update existing settings
    const formSettingsUpdate = settings as AppDatabase['public']['Tables']['form_settings']['Update'];

    const { data, error } = await supabase
      .from('form_settings')
      .update(formSettingsUpdate as never)
      .eq('id', existingSettings.id)
      .select()
      .single();

    if (error) {
      logger.error('Error updating form settings:', error);
      throw new Error(error.message);
    }

    return data as FormSettings;
  } else {
    // Insert new settings
    const formSettingsInsert = {
      user_id: user.id,
      form_id_external: formIdExternal,
      ...settings,
    } as AppDatabase['public']['Tables']['form_settings']['Insert'];

    const { data, error } = await supabase
      .from('form_settings')
      .insert(formSettingsInsert as never)
      .select()
      .single();

    if (error) {
      logger.error('Error inserting form settings:', error);
      throw new Error(error.message);
    }

    return data as FormSettings;
  }
};
